import React from 'react';
import { getRiskLevel } from '@/utils/weeklyReportUtils';

interface Satellite {
  CDM_ID?: string;
  pId: number;
  pName: string;
  sName: string;
  dca: number;
  poc: number;
  tcaTime: string;
}

interface NuriSatellitesSectionProps {
  topNuriSatellites: Satellite[];
  isNuriSatellitesLoading: boolean;
  nuriSatellitesSortBy: string;
  onSortChange: (value: string) => void;
  onRowClick: (cdmId: string) => void;
}

const NuriSatellitesSection: React.FC<NuriSatellitesSectionProps> = ({
  topNuriSatellites,
  isNuriSatellitesLoading,
  nuriSatellitesSortBy,
  onSortChange,
  onRowClick,
}) => {
  const getSatelliteItemClasses = (riskClassName: string) => {
    const baseClasses =
      'border border-slate-200 rounded-xl p-4 flex justify-between items-center transition-all cursor-pointer gap-3 hover:bg-slate-50 hover:translate-x-1';
    if (riskClassName === 'critical') {
      return `${baseClasses} bg-red-50 border-red-200 border-l-4 border-l-red-600`;
    }
    if (riskClassName === 'high') {
      return `${baseClasses} bg-amber-50 border-amber-200 border-l-4 border-l-amber-500`;
    }
    if (riskClassName === 'low') {
      return `${baseClasses} bg-emerald-50 border-emerald-200 border-l-4 border-l-emerald-500`;
    }
    return baseClasses;
  };

  return (
    <div className='bg-white rounded-2xl p-4 w-full max-w-[1400px] shadow-lg'>
      <div className='flex justify-between items-center py-4 flex-wrap gap-4'>
        <div className='flex items-center gap-4'>
          <div className='w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-gradient-to-br from-red-100 to-red-200 flex-shrink-0 leading-none'>
            🛰️
          </div>
          <p className='text-lg font-bold text-slate-800'>
            Nuri-4 Satellite Conjunctions
          </p>
        </div>
        <div className='flex gap-3 items-center ml-auto'>
          <select
            className='py-2.5 px-4 border border-slate-200 rounded-lg bg-white'
            value={nuriSatellitesSortBy}
            onChange={e => onSortChange(e.target.value)}
          >
            <option value='DCA'>Sort by DCA (km)</option>
            <option value='TCA'>Sort by TCA (UTC)</option>
            <option value='Probability'>Sort by Risk (Pc)</option>
          </select>
        </div>
      </div>
      <div className='grid gap-3'>
        {isNuriSatellitesLoading ? (
          <div className='text-center py-10'>
            Loading Nuri satellite conjunctions...
          </div>
        ) : topNuriSatellites.length === 0 ? (
          <div className='text-center py-10'>
            No significant conjunctions for Nuri satellites.
          </div>
        ) : (
          topNuriSatellites.map(satellite => {
            const risk = getRiskLevel(satellite.dca);
            return (
              <div
                key={satellite.CDM_ID}
                className={getSatelliteItemClasses(risk.className)}
                onClick={() => satellite.CDM_ID && onRowClick(satellite.CDM_ID)}
              >
                <div className='w-full'>
                  <p className='text-lg font-semibold mb-0'>
                    <span
                      style={{ color: risk.color }}
                      className='text-[28px] font-bold'
                    >
                      {satellite.dca.toFixed(3)} km
                    </span>
                  </p>
                  <div
                    className='flex w-full flex-row gap-1 h-fit rounded-lg p-1 mb-2'
                    style={{
                      backgroundColor: risk.color + '15',
                      borderColor: risk.color,
                    }}
                  >
                    <div className='text-black flex gap-2 items-center justify-center'>
                      <div className='bg-white border rounded-md px-2 py-0.5 text-sm flex gap-1'>
                        {satellite.pName.split('(')[0].trim()} / {satellite.pId}
                      </div>
                      <span
                        style={{
                          color: risk.color,
                        }}
                        className='text-sm font-normal'
                      >
                        ● {risk.level}
                      </span>
                    </div>
                  </div>
                  <div className='text-sm text-slate-500'>
                    <div style={{ color: risk.color }}>
                      Secondary: {satellite.sName}
                    </div>
                    <div>
                      <span className='text-sm text-slate-500'>
                        Pc:{' '}
                        {satellite.poc ? satellite.poc.toExponential(2) : 'N/A'}
                      </span>
                      {' / '}
                      TCA: {satellite.tcaTime.replace('\n', ' ').split('.')[0]}
                    </div>
                  </div>
                </div>
                <div className='text-right'></div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default NuriSatellitesSection;
